import React, { useState } from 'react'
import ScreenLoading from 'components/ScreenLoading'
import DropDownField from 'components/DropDownField'

const PreferencesTab = () => {
  const [language, setLanguage] = useState('en')
  const [timezone, setTimezone] = useState('UTC')
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [pendingRequest, setPendingRequest] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState('')
  const [error, setError] = useState('')

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setPendingRequest(true)
    setSaveSuccess('')
    setError('')

    try {
      // TODO: Implement API call when backend endpoint is ready
      // await updateUserPreferences({ language, timezone, email_notifications: emailNotifications })

      setSaveSuccess('Preferences updated successfully')
    } catch (err) {
      setError('Failed to update preferences')
    } finally {
      setPendingRequest(false)
    }
  }

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Dubai',
    'Australia/Sydney',
  ]

  return (
    <div style={{ marginTop: '1rem', width: '100%', maxWidth: '700px', padding: '0 1rem', boxSizing: 'border-box' }}>
      <form onSubmit={handleSave}>
        <div style={{ marginBottom: '2rem' }}>
          <DropDownField
            label="Language / Region"
            options={[
              { label: 'English (US)', value: 'en' },
              { label: 'English (UK)', value: 'en-gb' },
              { label: 'Español', value: 'es' },
              { label: 'Français', value: 'fr' },
              { label: 'Deutsch', value: 'de' },
              { label: 'Italiano', value: 'it' },
              { label: 'Português', value: 'pt' },
              { label: '日本語', value: 'ja' },
              { label: '中文', value: 'zh' }
            ]}
            value={language}
            onChange={setLanguage}
            style={{ marginBottom: '0.5rem' }}
          />
          <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
            This affects date formats, number formatting, and interface language
          </small>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <DropDownField
            label="Timezone"
            options={timezones.map((tz) => ({ label: tz, value: tz }))}
            value={timezone}
            onChange={setTimezone}
            style={{ marginBottom: '0.5rem' }}
          />
          <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
            All dates and times will be displayed in this timezone
          </small>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <div
            style={{
              padding: '1.5rem',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
              }}
            >
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <h4 style={{ margin: '0 0 0.5rem 0', wordBreak: 'break-word' }}>Email Notifications</h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#6c757d', wordBreak: 'break-word' }}>
                  Receive important updates and alerts via email
                </p>
              </div>
              <label
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: 'clamp(50px, 10vw, 60px)',
                  height: 'clamp(28px, 6vw, 34px)',
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: emailNotifications ? '#28a745' : '#ccc',
                    transition: '0.4s',
                    borderRadius: '34px',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      content: '""',
                      height: 'calc(100% - 8px)',
                      width: 'calc(100% - 8px)',
                      maxWidth: '26px',
                      maxHeight: '26px',
                      left: emailNotifications ? 'calc(100% - 30px)' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%',
                    }}
                  />
                </span>
              </label>
            </div>

            {emailNotifications && (
              <div
                style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e0e0e0',
                }}
              >
                <p
                  style={{
                    margin: '0 0 0.75rem 0',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#495057',
                  }}
                >
                  You'll receive emails for:
                </p>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '1.5rem',
                    fontSize: '0.85rem',
                    color: '#6c757d',
                    lineHeight: '1.8',
                  }}
                >
                  <li>Data synchronization completed</li>
                  <li>Failed sync attempts</li>
                  <li>New invitations to organizations</li>
                  <li>Important security alerts</li>
                  <li>Platform updates and maintenance</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {error && <p className="form-error">{error}</p>}
        {saveSuccess && <p className="form-success">{saveSuccess}</p>}

        <button
          type="submit"
          className="submit-button"
          disabled={pendingRequest}
          style={{ marginTop: '1rem', minWidth: '160px' }}
        >
          {pendingRequest ? <ScreenLoading spinnerColor="white" /> : 'Save Preferences'}
        </button>
      </form>

      <div
        style={{
          marginTop: '3rem',
          padding: '1.5rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
        }}
      >
        <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="material-icons-outlined">info</span>
          About Preferences
        </h4>
        <p style={{ fontSize: '0.9rem', color: '#6c757d', lineHeight: '1.6', margin: 0, wordBreak: 'break-word' }}>
          Your preferences are saved to your account and will apply across all devices. Changes
          take effect immediately after saving.
        </p>
      </div>
    </div>
  )
}

export default PreferencesTab
